// SEND-269 — A RUN WITH NO SCREEN IS STILL A RUN.
//
// EST-915610: the import POST got no response (the door was reading the
// PDF on the event loop and outlived the browser's clock), so the client
// never held a run_id. The import was alive on the server with nobody
// watching it: no banner, no words, no way back.
//
// This strip ASKS what is in flight and offers the two honest ways out:
// watch it, or fail it by name. It NEVER applies anything, never touches
// a quantity and never invents a stage — the words come from the run.
//
// It lives in its OWN FILE on purpose: SEND-135's lane lock forbids any
// effect inside HoverImportButton.jsx (the restore must never run on
// mount), and this list must load on mount.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function HoverInflightRuns({
  refreshKey = 0,
  busy = false,
  onWatch,
  stageLabel = {},
}) {
  const [runs, setRuns] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/estimates/hover-import/inflight",
                                     { timeout: 15000 });
      setRuns(Array.isArray(data?.runs) ? data.runs : []);
    } catch {
      // A list we could not fetch is evidence of nothing — say nothing.
      setRuns([]);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const abandon = async (runId) => {
    try {
      const { data } = await api.post(`/estimates/hover-import/${runId}/abandon`);
      toast.error(data?.error
                  || "This import was marked failed. Nothing was imported.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message
                  || "Could not mark this import failed.");
    } finally {
      load();
    }
  };

  if (!runs.length) return null;

  return (
    <div
      className="text-[10px] leading-snug border border-[#0EA5E9] bg-[#F0F9FF] text-[#0C4A6E] px-2 py-1.5 flex flex-col gap-1"
      data-testid="hover-inflight-strip"
    >
      <b className="uppercase">
        {runs.length === 1
          ? "1 HOVER import is still running on the server"
          : `${runs.length} HOVER imports are still running on the server`}
      </b>
      {runs.map((r) => (
        <div
          key={r.run_id}
          className="flex items-center gap-1.5 flex-wrap"
          data-testid={`hover-inflight-run-${r.run_id}`}
        >
          <span>
            {r.pdf_name || "PDF"} — {stageLabel[r.stage] || r.stage || "running"}
            {typeof r.elapsed_ms === "number"
              ? ` · ${Math.round(r.elapsed_ms / 1000)}s`
              : ""}
          </span>
          <button
            type="button"
            className="px-1.5 py-0.5 border border-[#0EA5E9] text-[#0369A1] uppercase font-bold tracking-wider disabled:opacity-50"
            onClick={() => onWatch && onWatch(r.run_id)}
            disabled={busy || typeof onWatch !== "function"}
            data-testid={`hover-inflight-resume-${r.run_id}`}
          >
            Watch it
          </button>
          <button
            type="button"
            className="px-1.5 py-0.5 border border-[#DC2626] text-[#B91C1C] uppercase font-bold tracking-wider"
            onClick={() => abandon(r.run_id)}
            data-testid={`hover-inflight-abandon-${r.run_id}`}
          >
            Mark failed
          </button>
        </div>
      ))}
    </div>
  );
}
